// src/context/BillsContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { API_URL } from "../config"; // ← THIS IS THE KEY FIX

const BillsContext = createContext();

export const useBills = () => {
  const context = useContext(BillsContext);
  if (!context) {
    throw new Error("useBills must be used within a BillsProvider");
  }
  return context;
};

export const BillsProvider = ({ children }) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBills = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setLoading(false);
      setError("No user ID found. Please log in.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_URL}/api/bills/get?userId=${userId}`, {
        method: "GET",
        credentials: "include", // Critical for session cookies in production
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        if (res.status === 401) {
          setError("Session expired. Please log in again.");
          // Optional: redirect to login
          // window.location.href = "/login";
        }
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      if (data.success && Array.isArray(data.data)) {
        const sortedBills = data.data.sort(
          (a, b) =>
            new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
        );
        setBills(sortedBills);
      } else {
        console.warn("No bills or invalid response:", data);
        setBills([]);
      }
    } catch (err) {
      console.error("Failed to fetch bills:", err);
      setError("Failed to load bills. Please try again.");
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  // Load bills when component mounts
  useEffect(() => {
    fetchBills();
  }, []);

  const addBill = (newBill) => {
    setBills((prev) => {
      const exists = prev.some((bill) => bill._id === newBill._id);
      if (exists) {
        return prev.map((bill) => (bill._id === newBill._id ? newBill : bill));
      }
      return [newBill, ...prev];
    });
  };

  const updateBill = (updatedBill) => {
    setBills((prev) =>
      prev.map((bill) => (bill._id === updatedBill._id ? updatedBill : bill))
    );
  };

  const deleteBill = (billId) => {
    setBills((prev) => prev.filter((bill) => bill._id !== billId));
  };

  const refetchBills = async () => {
    await fetchBills();
  };

  const value = {
    bills,
    loading,
    error,
    addBill,
    updateBill,
    deleteBill,
    refetchBills,
    fetchBills, // optional: expose if needed elsewhere
  };

  return (
    <BillsContext.Provider value={value}>{children}</BillsContext.Provider>
  );
};
